import { Table, TableContainer, InlineLoading } from '@carbon/react';
import { PersistentPagination } from 'components/persistent-pagination';
import { Pagination } from 'components/pagination';
import {
  itemRangeText,
  pageRangeText,
} from 'components/search/common/format-helpers';
import { ComponentProps, FC, ReactNode } from 'react';
import { EmptyState } from 'components/empty-state';
import { Default } from 'components/icons';

const PAGE_SIZES = [16, 24, 48, 64];

interface PaginationParams {
  page: number;
  pageSize: number;
}

interface Props extends ComponentProps<typeof Table> {
  pageSize: number;
  page: number;
  isLoading: boolean;
  loadingComponent?: ReactNode;
  emptyStateComponent?: ReactNode;
  totalItems: number;
  pageSizes?: number[];
  onPaginate: (params: PaginationParams) => void;
  toolbar?: ReactNode;
  paginatorOutsideTableContainer?: boolean;
  paginationOnTop?: boolean;
  persistentPagination?: boolean;
  scrollToTop?: boolean;
}

const PaginatedTable: FC<Props> = ({
  children,
  pageSize,
  pageSizes = PAGE_SIZES,
  page,
  isLoading,
  loadingComponent,
  emptyStateComponent,
  totalItems,
  onPaginate,
  toolbar,
  paginatorOutsideTableContainer,
  persistentPagination,
  paginationOnTop,
  scrollToTop = true,
  ...rest
}) => {
  // Ensure pageSize is valid
  const currentPageSize = pageSizes.includes(pageSize)
    ? pageSize
    : pageSizes[0];

  // Ensure page is within bounds
  const totalPages = Math.ceil(totalItems / currentPageSize);
  const currentPage = page >= 1 && page <= totalPages ? page : 1;

  const handlePageChange = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    const validatedPageSize = pageSizes.includes(pageSize)
      ? pageSize
      : currentPageSize;
    const validatedPage =
      page >= 1 && page <= Math.ceil(totalItems / validatedPageSize) ? page : 1;

    if (scrollToTop) {
      window.scrollTo(0, 0);
    }
    onPaginate({ page: validatedPage, pageSize: validatedPageSize });
  };

  const paginator = totalItems > 0 && (
    <>
      {persistentPagination ? (
        <PersistentPagination
          pageSizes={pageSizes}
          pageSize={currentPageSize}
          page={currentPage}
          totalItems={totalItems}
          onChange={handlePageChange}
          itemRangeText={itemRangeText}
          pageRangeText={pageRangeText}
        />
      ) : (
        <Pagination
          pageSizes={pageSizes}
          pageSize={currentPageSize}
          page={currentPage}
          totalItems={totalItems}
          onChange={handlePageChange}
          itemRangeText={itemRangeText}
          pageRangeText={pageRangeText}
        />
      )}
    </>
  );

  return (
    <>
      {paginationOnTop && paginatorOutsideTableContainer && paginator}
      <TableContainer>
        {paginationOnTop && !paginatorOutsideTableContainer && paginator}
        {toolbar}
        {isLoading ? (
          loadingComponent || <InlineLoading />
        ) : totalItems > 0 ? (
          <Table {...rest}>{children}</Table>
        ) : (
          emptyStateComponent || (
            <EmptyState
              headerText="No data"
              helperText="We couldn't find any available data."
              IconComponent={Default}
            />
          )
        )}
        {!paginatorOutsideTableContainer && paginator}
      </TableContainer>
      {paginatorOutsideTableContainer && paginator}
    </>
  );
};

export { PaginatedTable };
